import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ax/Button";
import { Field } from "@/components/ax/Field";
import { Switch } from "@/components/ax/Switch";
import { Card } from "@/components/account/PreferencesCard";
import { QuietConfirm } from "@/components/account/SubscriptionCard";
import { useFlash } from "@/hooks/useFlash";
import { useI18n } from "@/i18n";
import { betaRelease } from "../ax/BetaNotice";
import { changePassword, getConsent, setConsent } from "@/lib/mockAuth";
import { auth, functions } from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";
import { signOut } from "firebase/auth";
import { Database, Download, KeyRound } from "lucide-react";
import styles from "@/components/account/AccountVisual.module.css";
import { MfaSettings } from "./MfaSettings";
import { MfaChallenge } from "./MfaChallenge";
import { reauthenticateAccount, resolveTotpChallenge } from "@/lib/accountSecurity";

const KEYS = ["data", "ai", "analytics"];

export const PrivacyCard = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [consent, setLocal] = useState(() => ({ data: true, ai: false, analytics: false, ...getConsent() }));
  const [failed, setFailed] = useState(false);
  const [savingConsent, setSavingConsent] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [del, setDel] = useState(false);
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const toggle = (key) => async (next) => {
    if (key === "data" || savingConsent) return;
    setFailed(false);
    setSavingConsent(true);
    const prev = consent;
    const value = { ...consent, [key]: next };
    setLocal(value);
    const res = await setConsent(value, { fail: new URLSearchParams(window.location.search).get("fail") === "consent" });
    if (res.error) {
      setLocal(prev);
      setFailed(true);
    }
    setSavingConsent(false);
  };

  const exportJson = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const fn = httpsCallable(functions, "exportAccount");
      const res = await fn({});
      const payload = res.data?.data || res.data;
      const exportData = {
        exportedAt: payload.exportedAt || new Date().toISOString(),
        consent: payload.consent || consent,
        decisions: payload.decisions || [],
        highlights: payload.highlights || [],
      };
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: "application/json",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "axoris-export.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setExportError(t("acc.exportFail") || "Gagal mengekspor data akun. Coba lagi nanti.");
    } finally {
      setExporting(false);
    }
  };

  const confirmDelete = async () => {
    if (typed !== "HAPUS") return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const fn = httpsCallable(functions, "deleteAccount");
      await fn({ confirmation: "HAPUS" });
      try {
        await signOut(auth);
      } catch {}
      localStorage.clear();
      sessionStorage.clear();
      navigate("/masuk", { replace: true });
    } catch (err) {
      setDeleteError(t("err.delete"));
      setDeleting(false);
    }
  };

  return (
    <Card id="acc-privacy" title={t("acc.privacy")} testId="account-privacy" icon={Database} busy={savingConsent || exporting || deleting} attention={failed || !!exportError || !!deleteError}>
      <ul className={styles.consents}>
        {KEYS.map((k) => (
          <li key={k} className={styles.consentRow}>
            <div className={styles.consentText}>
              <p id={`priv-${k}`} className={styles.consentTitle}>{t(`consent.${k}.title`)}</p>
              {k === "data" && <p className={styles.required}>{t("consent.data.required")}</p>}
            </div>
            <Switch checked={consent[k]} disabled={k === "data" || savingConsent} busy={k !== "data" && savingConsent} onChange={toggle(k)} labelledBy={`priv-${k}`} testId={`priv-${k}-switch`} />
          </li>
        ))}
      </ul>
      {failed && <p role="alert" data-testid="privacy-error" className="m-0 mt-2 text-sm leading-5 text-ax-error">{t("consent.fail")}</p>}

      <div className={styles.exportBlock}>
        <div className={styles.exportRow}>
          <div className={styles.exportText}>
            <p className={styles.exportTitle}>{t("acc.exportTitle")}</p>
            <p className={styles.exportNote}>{t("acc.exportNote")}</p>
          </div>
        <Button
          onClick={exportJson}
          busy={exporting}
          disabled={exporting}
          data-testid="export-json-button"
        >
          <Download size={16} strokeWidth={1.5} aria-hidden="true" />
          {exporting ? t("acc.exporting") : t("acc.export")}
        </Button>
        </div>
        {exportError && (
          <p role="alert" data-testid="export-error" className="m-0 mt-2 text-sm leading-5 text-ax-error">
            {exportError}
          </p>
        )}
      </div>

      <div className={styles.deleteAction}><Button
        variant="ghost"
        onClick={() => {
          setDel(true);
          setTyped("");
          setDeleteError(null);
        }}
        data-testid="delete-account-button"
        disabled={betaRelease}
        className="px-0"
      >
        {t("acc.delete")}
      </Button></div>
      {betaRelease && <p className="mt-2 text-sm leading-5 text-ax-muted">{t("beta.deleteUnavailable")}</p>}

      {del && (
        <QuietConfirm
          testId="delete-sheet"
          title={t("acc.delete")}
          body={t("acc.deleteBody")}
          confirmLabel={deleting ? t("acc.deleting") : t("acc.delete")}
          keepLabel={t("acc.keepAccount")}
          disabled={typed !== "HAPUS" || deleting}
          busy={deleting}
          error={deleteError}
          onConfirm={confirmDelete}
          onClose={() => {
            if (deleting) return;
            setDel(false);
            setTyped("");
            setDeleteError(null);
          }}
        >
          <Field
            className="mt-4"
            label={t("acc.deleteField")}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            error={typed && typed !== "HAPUS" ? t("acc.deleteHint") : undefined}
            hint={t("acc.deleteHint")}
            autoComplete="off"
            testId="delete-confirm-input"
          />
        </QuietConfirm>
      )}
    </Card>
  );
};

export const SecurityCard = () => {
  const { t } = useI18n();
  const [pw, setPw] = useState("");
  const [error, setError] = useState();
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useFlash(2000);
  const [mfaBusy, setMfaBusy] = useState(false);
  const [reauth, setReauth] = useState(false), [currentPw, setCurrentPw] = useState(""), [passwordChallenge, setPasswordChallenge] = useState(null);
  const passwordProvider = auth.currentUser?.providerData.some((provider) => provider.providerId === "password");

  const writePassword = async () => {
    const res = await changePassword(pw);
    if (res.code === "auth/requires-recent-login") { setReauth(true); setCurrentPw(""); return { error: "reauth" }; }
    if (!res?.ok) { setError(t("acc.pwFailed")); return res; }
    setPw(""); setCurrentPw(""); setReauth(false); setPasswordChallenge(null);
    setFlash(t("acc.pwSaved")); return res;
  };

  const save = async () => {
    if (busy) return;
    setFlash("");
    setError();
    if (pw.length < 8) return setError(t("err.password.min"));
    setBusy(true);
    if (reauth) {
      const result = await reauthenticateAccount(currentPw);
      setCurrentPw("");
      if (result.mfa) { setPasswordChallenge(result.mfa); setBusy(false); return; }
      if (!result.ok) { setError(t("mfa.actionFailed")); setBusy(false); return; }
    }
    await writePassword();
    setBusy(false);
  };

  const verifyPasswordIdentity = async (uid, code) => {
    setBusy(true);
    const result = await resolveTotpChallenge(passwordChallenge, uid, code);
    const saved = result.ok ? await writePassword() : result;
    setBusy(false); return saved;
  };

  return (
    <Card id="acc-security" title={t("acc.security")} testId="account-security" icon={KeyRound} busy={busy || mfaBusy} attention={!!error}>
      <Field
        label={t("acc.newPw")}
        hint={t("acc.passwordHint")}
        type="password"
        autoComplete="new-password"
        maxLength={72}
        value={pw}
        onChange={(e) => {
          setPw(e.target.value);
          setError();
        }}
        error={error}
        testId="new-password-input"
        disabled={busy || mfaBusy}
        className={styles.passwordField}
      />
      {reauth && !passwordChallenge && <div className="mt-4" data-testid="password-reauth">
        <p className={styles.totpNote}>{t("mfa.passwordReauth")}</p>
        {passwordProvider && <Field className="mt-3" label={t("mfa.currentPassword")} type="password" autoComplete="current-password" value={currentPw} onChange={(event) => setCurrentPw(event.target.value)} disabled={busy} testId="password-current-input" />}
      </div>}
      {passwordChallenge && <MfaChallenge challenge={passwordChallenge} onVerify={verifyPasswordIdentity} onCancel={() => { setPasswordChallenge(null); setCurrentPw(""); }} />}
      <div className={styles.securityActions}>
      {!passwordChallenge && <Button busy={busy} disabled={busy || mfaBusy || !pw || (reauth && passwordProvider && !currentPw)} onClick={save} data-testid="change-password-button">
        {t(reauth ? "mfa.continue" : "acc.pwSave")}
      </Button>}
      </div>
      <p role="status" data-testid="security-flash" className={styles.flash}>{flash}</p>
      <MfaSettings onBusy={setMfaBusy} disabled={busy || !!passwordChallenge} />
    </Card>
  );
};
